import { FetchPolicy } from '@constants/FetchPolicy';
import { Grid, withStyles } from '@material-ui/core';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import { SkillRatingsLearnMoreDrawer } from '@talentnow/shared-ui/components';
import { allCountries } from 'country-region-data';
import moment from 'moment-timezone';
import React, { Component } from 'react';

import {
  candidateFormRequirement as CandidateFormRequirementResponse,
  getActiveVendorSubscription as GetActiveVendorSubscriptionQueryType,
  getAvailablePrescreens as GetAvailablePrescreensResponse,
  getAvailablePrescreensVariables,
  GetCandidateById as GetCandidateByIdResponse,
  GetRehirePrefill as GetRehirePrefillResponse,
  GetRehirePrefillVariables,
  getShoreAndWorkLocationTypes as GetShoreAndWorkLocationTypesResponse,
  getVendorForUser as GetVendorForUserType,
  ParseResume_parseResume_education,
  ParseResume_parseResume_experience,
  ParseResume as ParseResumeType,
  GetRehirePrefill_getRehirePrefill as RehirePrefill,
  validatesNameAndSsn as ValidatesNameAndSsnResponse,
} from '../../../__generated__/types';
import { SelectOption, SelectOptionChangeParam } from '../../../_components/forms/SelectDropdown';
import Loader from '../../../_components/Loader';
import { NameValuePair } from '../../../_components/NameValuePair';
import TnSnackBar, { SnackBarVariant } from '../../../_components/snackbar/TnSnackBar';
import TnButton from '../../../_components/tn-button/TnButton';
import TnModal from '../../../_components/tn-modal/TnModal';
import { PrescreenOption, toPrescreenOption } from '../../../_helpers/PrescreenHelper';
import { getOnlyNumbers, isValidNumber } from '../../../_helpers/validators';
import * as DocumentTypes from '../../../_services/candidateDocuments/DocumentTypes';
import { generateDocumentsDataV2 } from '../../../_services/candidateDocuments/GenerateDocuments';
import {
  CandidateFormRequirement,
  EditCandidate,
  GetCandidateByIdQuery,
  GetRehirePrefillQuery,
  GetShoreAndWorkLocationTypes,
  ParseResume,
  SubmitCandidate,
  ValidatesNameAndSsn,
} from '../../../_services/candidates/queries';
import { GetAvailablePrescreens } from '../../../_services/jobs/queries';
import { client } from '../../../index';

import CandidateDetails, { SUMMARY_MAX_LENGTH } from './CandidateDetails';
import { EducationEntity } from './Education/EducationForm';
import { Employment } from './Employment/EmploymentForm';
import RateAndJob from './RateAndJob';
import ResumeAndUploads from './ResumeAndUploads';
import SkillsAndEducation from './SkillsAndEducation';
import { CandidateSkill } from './SkillsTable';
import Stepper, { StepDef, StepStatus } from './Stepper';

import { getConfigSetting } from '@components/configuration/configuration';
import { ConfigName } from '@constants/ConfigName';
import { GetActiveVendorSubscription, GetVendorIdForUser } from '@services/vendor/queries';
import { history } from '../../../_helpers/history.js';

type FieldErrors = Record<string, string>;

interface AddressSuggestion {
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

interface CandidateDocument {
  document_category_id: string;
  document_name: string;
  document_url: string;
  data_id?: string;
}

interface ClientHiringCompany {
  label: string;
  value: string;
  clientId: string;
}

interface Candidate {
  id?: string;
  first_name: string;
  last_name: string;
  ssn: string;
  email_id: string;
  sub_contractor_firm: string;
  summary: string;
  candidate_documents: CandidateDocument[];
  rate_frequency: SelectOption | null;
  rate_frequency_id: string;
  contact_id: string;
  interview_availability: string;
  earliest_start_date: string | null;
  phone_number: string;
  linkedin_url: string;
  skype_id: string;
  status: string;
  submission_rate: number | null;
  vendor_id: string;
  clients: SelectOption[];
  client_ids: string[];
  client_hiring_companies: ClientHiringCompany[];
  client_hiring_company_ids: string[];
  job_uuid: SelectOption | null;
  hirevue_position: PrescreenOption | null;
  location: string;
  additional_information: string | null;
  mark_up_percentage: number | null;
  phone_type: string | null;
  state: NameValuePair | null;
  country: NameValuePair | null;
  street_address: string;
  city: string;
  zip_code: string;
  shore_type_preferences: SelectOption[];
  work_location_type_preferences: SelectOption[];
  estimated_hours: number | null;
  work_authorized: SelectOption | null;
  visa_required: SelectOption | null;
  skills: CandidateSkill[];
  employment_history: Employment[];
  education_history: EducationEntity[];
}

interface CandidateModalProps {
  noWorkspaceAccess?: boolean;
  open: boolean;
  handleClose: (submitted: boolean) => void;
  mode?: 'create' | 'edit';
  candidateId?: string;
  contractorId?: string;
  workspaceId?: string;
  classes?: any;
  isConfirmRehire?: boolean;
}

interface CandidateModalState {
  candidate: Candidate;
  currentStep: number;
  jobTitleOptions: SelectOption[];
  prescreenOptions: PrescreenOption[];
  rateFrequencyOptions: SelectOption[];
  clientOptions: SelectOption[];
  clientHiringCompanies: ClientHiringCompany[];
  shoreTypeOptions: NameValuePair[];
  workLocationTypeOptions: NameValuePair[];
  stateOptions: NameValuePair[];
  rowData: any[];
  emptyRowData: any[];
  resumeId: string;
  resumeCount: number;
  isSubmittedDisabled: boolean;
  showLoader: boolean;
  showMessage: boolean;
  messageContent: string;
  messageVariant: SnackBarVariant;
  visitedSteps: [boolean, boolean, boolean, boolean];
  learnMoreDrawerOpen: boolean;
  skillsEducationFormOpen: boolean;
  skillsEducationFormValid: boolean;
  skillsEducationInvalidFields: string[];
  resumeParseComplete: boolean;
  resumeParseError: boolean;
  editDataLoaded: boolean;
  touchedFields: Record<string, boolean>;
  allowedShoreTypes: NameValuePair[];
  prescreenEnabled: boolean;
  allowedClients: SelectOption[];
  requirementsLoaded: boolean;
  locationTypesLoaded: boolean;
  rehirePrefill: RehirePrefill | null;
}

const phoneTypeOptions = [
  { label: 'Android', value: 'ANDROID' },
  { label: 'iPhone', value: 'IPHONE' },
  { label: 'Other', value: 'OTHER' },
];

const workAuthorizedOptions = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];

const visaRequiredOptions = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];

const REQUIRED_FIELD_MESSAGE = 'This field is required';
const PHONE_NUMBER_MESSAGE = 'Phone Number must be 10 digits';
const invalidValueMessage = (label: string) => `Enter a valid ${label} value`;

const NAME_MAX_LENGTH = 50;
const SSN_LENGTH = 4;
const PHONE_NUMBER_LENGTH = 10;
const SUMMARY_MAX_LENGTH_MESSAGE = `Summary exceeds character limit. Please shorten your text to ${SUMMARY_MAX_LENGTH} characters or less.`;

const RESUME_AND_UPLOADS_FIELD_LABELS: Record<string, string> = {
  resume: 'Resume',
};

const CANDIDATE_DETAILS_FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  ssn: 'Last 4 digits of SSN',
  email: 'Email',
  summary: 'Candidate Summary',
  workAuthorized: 'Work Authorized',
  visaRequired: 'Visa Required',
  meetingAvailability: 'Meeting Availability',
  phoneNumber: 'Phone Number',
  country: 'Country',
  city: 'City',
  state: 'State/Region/Province',
};

const SKILLS_AND_EDUCATION_FIELD_LABELS: Record<string, string> = {
  openForm: 'Unsaved Entry',
  educationHistory: 'Education History',
  employmentHistory: 'Employment History',
};

const RATE_AND_JOB_FIELD_LABELS: Record<string, string> = {
  rate: 'Rate',
  rateFrequency: 'Frequency',
  earliestStartDate: 'Earliest Start Date',
  job: 'Job Title',
  clients: 'Preferred Clients',
  hiringCompanies: 'Hiring Companies',
  shoreTypePreferences: 'Shore Type Preferences',
  workLocationTypePreferences: 'Work Location Type Preferences',
};

const STEP_FIELD_LABELS: Array<Record<string, string>> = [
  RESUME_AND_UPLOADS_FIELD_LABELS,
  CANDIDATE_DETAILS_FIELD_LABELS,
  SKILLS_AND_EDUCATION_FIELD_LABELS,
  RATE_AND_JOB_FIELD_LABELS,
];

const getInvalidFieldNames = (errors: FieldErrors, labels: Record<string, string>): string[] =>
  Object.keys(errors).map((field) => labels[field] ?? field);

const booleanToSelectOption = (value: boolean | null | undefined): SelectOption | null => {
  if (value === null || value === undefined) return null;

  return value ? { label: 'Yes', value: 'YES' } : { label: 'No', value: 'NO' };
};

const isValidEmail = (email: string) => {
  const regEmail =
    // eslint-disable-next-line no-useless-escape
    /^(?:[\w\!\#\$\%\&\'\*\+\-\/\=\?\^\`\{\|\}\~]+\.)*[\w\!\#\$\%\&\'\*\+\-\/\=\?\^\`\{\|\}\~]+@(?:(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-](?!\.)){0,61}[a-zA-Z0-9]?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9\-](?!$)){0,61}[a-zA-Z0-9]?)|(?:\[(?:(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\.){3}(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\]))$/;

  return regEmail.test(email);
};

class CandidateModal extends Component<CandidateModalProps, CandidateModalState> {
  constructor(props: CandidateModalProps) {
    super(props);
    this.state = {
      candidate: this.initializeCandidate(),
      currentStep: 0,
      jobTitleOptions: [],
      prescreenOptions: [],
      rateFrequencyOptions: [],
      clientOptions: [],
      clientHiringCompanies: [],
      shoreTypeOptions: [],
      workLocationTypeOptions: [],
      stateOptions: [],
      rowData: [],
      emptyRowData: [],
      resumeId: '',
      resumeCount: 0,
      isSubmittedDisabled: true,
      showLoader: props.mode === 'edit',
      showMessage: false,
      messageContent: '',
      messageVariant: 'success',
      visitedSteps: [false, false, false, false],
      learnMoreDrawerOpen: false,
      skillsEducationFormOpen: false,
      skillsEducationFormValid: true,
      skillsEducationInvalidFields: [],
      resumeParseComplete: false,
      resumeParseError: false,
      editDataLoaded: props.mode !== 'edit',
      touchedFields: {},
      allowedShoreTypes: [],
      prescreenEnabled: false,
      allowedClients: [],
      requirementsLoaded: false,
      locationTypesLoaded: false,
      rehirePrefill: null,
    };
  }

  initializeCandidate() {
    return {
      first_name: '',
      last_name: '',
      ssn: '',
      email_id: '',
      sub_contractor_firm: '',
      summary: '',
      candidate_documents: [],
      rate_frequency: { label: '', value: '' },
      rate_frequency_id: '',
      contact_id: '',
      interview_availability: '',
      earliest_start_date: null,
      phone_number: '',
      linkedin_url: '',
      skype_id: '',
      status: '',
      submission_rate: null,
      vendor_id: '',
      clients: [],
      client_ids: [],
      client_hiring_companies: [],
      client_hiring_company_ids: [],
      job_uuid: { label: '', value: '' },
      hirevue_position: null,
      location: '',
      additional_information: null,
      mark_up_percentage: null,
      phone_type: null,
      state: null,
      country: null,
      street_address: '',
      city: '',
      zip_code: '',
      shore_type_preferences: [],
      work_location_type_preferences: [],
      estimated_hours: null,
      work_authorized: null,
      visa_required: null,
      skills: [],
      employment_history: [],
      education_history: [],
    };
  }

  async componentDidMount() {
    const subscriptionRestrictionEnabled = await getConfigSetting(ConfigName.SUBSCRIPTION_TIER_RESTRICTION)
      .then((subscriptionRestrictionEnabled: boolean) => {
        return subscriptionRestrictionEnabled;
      })
      .catch(() => {
        return false;
      });

    const prescreenEnabled = await getConfigSetting(ConfigName.PRESCREEN)
      .then((result: boolean) => {
        return result;
      })
      .catch(() => {
        return false;
      });

    this.setState({ prescreenEnabled });

    await this.loadFormFields();
    await this.fetchShoreAndWorkLocationTypes();
    await this.fetchVendorSubscription(subscriptionRestrictionEnabled);
  }

  loadFormFields = async () => {
    await client
      .query<CandidateFormRequirementResponse>({
        query: CandidateFormRequirement,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
      })
      .then((data) => {
        const jobTitleOptions: SelectOption[] = [];
        const rateFrequencyOptions: SelectOption[] = [];
        const clientOptions: SelectOption[] = [];
        const clientHiringCompanies: ClientHiringCompany[] = [];
        const rowData: any[] = [];

        data?.data?.candidateFormRequirement?.jobTitle?.forEach((row) => {
          jobTitleOptions.push({ label: row.name ?? '', value: row.id ?? '' });
        });
        data?.data?.candidateFormRequirement?.rateFrequency?.forEach((row) => {
          rateFrequencyOptions.push({ label: row.name ?? '', value: row.id ?? '' });
        });
        const emptyRowData: any[] = [];
        data?.data?.candidateFormRequirement?.candidateDocumentCategory?.forEach((option) => {
          if (option.category === DocumentTypes.RESUME) {
            this.setState({ resumeId: option.id });
          }
          rowData.push(generateDocumentsDataV2(option));
          emptyRowData.push(generateDocumentsDataV2(option));
        });
        data?.data?.candidateFormRequirement?.clients?.forEach((row) => {
          clientOptions.push({ label: row.name ?? '', value: row.id ?? '' });
        });
        data?.data?.candidateFormRequirement?.clientHiringCompany?.forEach((row) => {
          const client_sublabel = row.workspace?.name ? ` (${row.workspace?.name})` : '';
          clientHiringCompanies.push({
            label: (row.name ?? '') + client_sublabel,
            value: row.id ?? '',
            clientId: row.workspace?.id ?? '',
          });
        });

        this.setState(
          {
            jobTitleOptions,
            rowData,
            emptyRowData,
            rateFrequencyOptions,
            clientOptions,
            allowedClients: clientOptions,
            clientHiringCompanies,
            requirementsLoaded: true,
          },
          () => {
            if (this.props.mode === 'edit') {
              this.handlePreFillEditCandidateData();
            }
          },
        );
      });
  };

  fetchShoreAndWorkLocationTypes = async () => {
    await client
      .query<GetShoreAndWorkLocationTypesResponse>({
        query: GetShoreAndWorkLocationTypes,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
      })
      .then((data) => {
        const shoreTypes =
          data?.data?.getShoreAndWorkLocationTypes?.shore_types?.map((s) => ({
            label: s.name,
            value: s.id,
          })) ?? [];
        const workLocationTypes =
          data?.data?.getShoreAndWorkLocationTypes?.work_location_types?.map((s) => ({
            label: s.name,
            value: s.id,
          })) ?? [];
        this.setState({
          workLocationTypeOptions: workLocationTypes,
          shoreTypeOptions: shoreTypes,
          locationTypesLoaded: true,
        });
      });
  };

  fetchVendorSubscription = async (subscriptionRestrictionEnabled: boolean) => {
    const { data: vendorData } = await client.query<GetVendorForUserType>({
      query: GetVendorIdForUser,
    });
    const vendorId = vendorData?.getVendorForUser?.id;

    if (!subscriptionRestrictionEnabled) {
      this.setState((prevState) => ({ allowedShoreTypes: prevState.shoreTypeOptions }));

      return;
    }

    await client
      .query<GetActiveVendorSubscriptionQueryType>({
        query: GetActiveVendorSubscription,
        variables: {
          vendorId,
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
      })
      .then((data) => {
        const subscription = data?.data?.getActiveVendorSubscription ?? null;

        // Client options are already limited to the subscription's clients by the candidate-form clients API
        if (['PRO', 'PREMIUM'].includes(subscription?.name ?? '')) {
          this.setState((prevState) => ({
            allowedShoreTypes: prevState.shoreTypeOptions.filter((shoreType) => shoreType.label === 'Onshore'),
          }));
        } else {
          this.setState((prevState) => ({ allowedShoreTypes: prevState.shoreTypeOptions }));
        }
      });
  };

  componentDidUpdate(prevProps: CandidateModalProps, prevState: CandidateModalState) {
    const { open, mode, contractorId, workspaceId } = this.props;
    const { requirementsLoaded, locationTypesLoaded, editDataLoaded, rehirePrefill } = this.state;

    if (open && !prevProps.open && !contractorId && rehirePrefill) {
      this.setState({ rehirePrefill: null });
    }

    // Load the existing profile before patching rehire values in edit mode.
    const isReady =
      open &&
      !!contractorId &&
      !!workspaceId &&
      requirementsLoaded &&
      locationTypesLoaded &&
      (mode !== 'edit' || editDataLoaded);
    const wasReady =
      prevProps.open &&
      prevProps.contractorId === contractorId &&
      prevProps.workspaceId === workspaceId &&
      prevState.requirementsLoaded &&
      prevState.locationTypesLoaded &&
      (prevProps.mode !== 'edit' || prevState.editDataLoaded);

    if (isReady && !wasReady) {
      this.handlePreFillRehirePrefill(contractorId as string, workspaceId as string);
    }
  }

  handlePreFillRehirePrefill = (contractorId: string, workspaceId: string) => {
    this.setState({ showLoader: true });
    client
      .query<GetRehirePrefillResponse, GetRehirePrefillVariables>({
        query: GetRehirePrefillQuery,
        variables: { contractor_id: contractorId, workspace_id: workspaceId },
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .then((data) => {
        const prefill = data?.data?.getRehirePrefill;
        if (!prefill) {
          this.setState({ showLoader: false });
          return;
        }
        if (this.props.mode === 'edit') {
          this.applyRehireStartDatePrefill(prefill);
          return;
        }
        this.applyRehirePrefill(prefill);
      })
      .catch(() => {
        this.setState({
          showLoader: false,
          showMessage: true,
          messageVariant: 'error',
          messageContent: 'Failed to load contractor details for rehire.',
        });
      });
  };

  buildRehireClientOptions = (contractor_workspace_id: string, contractor_workspace_name: string) => {
    const { clientOptions } = this.state;
    if (this.props.noWorkspaceAccess) {
      return contractor_workspace_id
        ? [{ label: contractor_workspace_name || 'Workspace Rehire', value: contractor_workspace_id }]
        : [];
    }
    if (!contractor_workspace_id || clientOptions.some((option) => option.value === contractor_workspace_id)) {
      return clientOptions;
    }

    const label = contractor_workspace_name ? `${contractor_workspace_name} (Workspace Rehire)` : 'Workspace Rehire';

    return [...clientOptions, { label, value: contractor_workspace_id }];
  };

  applyRehireStartDatePrefill = (prefill: RehirePrefill) => {
    const scheduledOffboardDate = prefill.placement?.scheduled_offboard_date;
    const allowedClients = this.buildRehireClientOptions(
      prefill.contractor_workspace_id ?? '',
      prefill.contractor_workspace_name ?? '',
    );
    this.setState(
      (prevState) => ({
        candidate: {
          ...prevState.candidate,
          clients: this.props.noWorkspaceAccess ? allowedClients : prevState.candidate.clients,
          earliest_start_date: scheduledOffboardDate
            ? moment.utc(scheduledOffboardDate).add(1, 'day').format('MM/DD/YYYY')
            : null,
        },
        allowedClients,
        clientHiringCompanies: this.getRehireClientHiringCompanies(prefill, prefill.contractor_workspace_id ?? ''),
        showLoader: false,
      }),
      () => this.setState({ isSubmittedDisabled: !this.validateFormFields() }),
    );
  };

  applyRehirePrefill = (prefill: RehirePrefill) => {
    const {
      placement,
      candidate: atsCandidate,
      contractor_workspace_id,
      contractor_workspace_name,
    } = prefill as RehirePrefill;
    const contact = atsCandidate?.contact;
    const { jobTitleOptions, rateFrequencyOptions, shoreTypeOptions, workLocationTypeOptions } = this.state;
    const allowedClients = this.buildRehireClientOptions(
      contractor_workspace_id ?? '',
      contractor_workspace_name ?? '',
    );
    const clientHiringCompanies = this.getRehireClientHiringCompanies(prefill, contractor_workspace_id ?? '');

    const { country, state, stateOptions } = this.getCountryAndStateOptions(contact?.country, contact?.state);

    const scheduledOffboardDate = placement?.scheduled_offboard_date;
    const matchesLabel = (option: SelectOption, label?: string | null) =>
      !!label && option.label.toLowerCase() === label.toLowerCase();

    const candidate: Candidate = {
      ...this.initializeCandidate(),
      first_name: contact?.first_name ?? '',
      last_name: contact?.last_name ?? '',
      email_id: contact?.email_id ?? '',
      phone_number: contact?.phone_number ?? '',
      phone_type: contact?.phone_type ? contact.phone_type.toUpperCase() : null,
      linkedin_url: contact?.linkedin_url ?? '',
      skype_id: contact?.skype_id ?? '',
      work_authorized: booleanToSelectOption(contact?.work_authorized),
      visa_required: booleanToSelectOption(contact?.visa_required),
      street_address: contact?.street_address ?? '',
      city: contact?.city ?? '',
      zip_code: contact?.zip_code ?? '',
      country,
      state,
      sub_contractor_firm: '',
      summary: atsCandidate?.summary ?? '',
      additional_information: null,
      interview_availability: '',
      estimated_hours: placement?.estimated_hours ?? null,
      submission_rate: placement?.vendor_rate ?? null,
      rate_frequency: rateFrequencyOptions.find((o) => matchesLabel(o, placement?.pay_frequency)) ?? null,
      earliest_start_date: scheduledOffboardDate
        ? moment.utc(scheduledOffboardDate).add(1, 'day').format('MM/DD/YYYY')
        : null,
      job_uuid: jobTitleOptions.find((o) => matchesLabel(o, placement?.job_title)) ?? null,
      clients: this.props.noWorkspaceAccess
        ? allowedClients.filter((option) => option.value === contractor_workspace_id)
        : [],
      shore_type_preferences: (shoreTypeOptions as SelectOption[]).filter((o) => o.value === placement?.shore_type_id),
      work_location_type_preferences: (workLocationTypeOptions as SelectOption[]).filter(
        (o) => o.value === placement?.work_location_type_id,
      ),
      skills:
        atsCandidate?.skills
          ?.filter((s) => !!s.name)
          .map((s) => ({
            name: s.name as string,
            proficiency_rating: s.proficiency_rating || 0,
            standout_skill: s.standout_skill || false,
            verified: s.verified || false,
            delete_icon: '',
          })) ?? [],
      employment_history:
        atsCandidate?.employment_history?.map((e) => ({
          companyName: e.company_name || '',
          jobTitle: e.job_title || '',
          startDate: e.start_date || '',
          endDate: e.end_date || '',
          additionalInfo: e.additional_information || '',
        })) ?? [],
      education_history:
        atsCandidate?.education_history?.map((e) => ({
          schoolName: e.school_name || '',
          degreeName: e.degree_name || '',
          startDate: e.start_date || '',
          endDate: e.end_date || '',
          gpa: e.gpa || null,
          additionalInfo: e.additional_information || '',
        })) ?? [],
    };

    const rowData = this.state.emptyRowData.map((doc) => ({ ...doc, files: [] as File[] }));

    this.setState(
      {
        candidate,
        allowedClients,
        clientHiringCompanies,
        stateOptions,
        rowData: [],
        resumeCount: 0,
        resumeParseComplete: true,
        rehirePrefill: prefill,
        showLoader: false,
      },
      () => this.setState({ rowData, isSubmittedDisabled: !this.validateFormFields() }),
    );
  };

  getRehireClientHiringCompanies = (prefill: RehirePrefill, workspaceId: string) => {
    const clientHiringCompanies = [...this.state.clientHiringCompanies];
    prefill.hiring_companies?.forEach((company) => {
      if (!company.id || !company.name) {
        return;
      }

      if (!clientHiringCompanies.some((option) => option.value === company.id && option.clientId === workspaceId)) {
        clientHiringCompanies.push({
          label: company.name,
          value: company.id,
          clientId: workspaceId,
        });
      }
    });

    return clientHiringCompanies;
  };

  getCountryAndStateOptions = (countryCode?: string | null, stateCode?: string | null) => {
    const countryIndex = allCountries.findIndex(([, code]: [string, string, string[][]]) => code === countryCode);
    if (countryIndex === -1) {
      return { country: null, state: null, stateOptions: [] as NameValuePair[] };
    }

    const [countryName, code, states] = allCountries[countryIndex];
    const stateOptions: NameValuePair[] = states.map(([stateName, stateValue]: [string, string]) => ({
      label: stateName,
      value: stateValue,
    }));

    return {
      country: { label: countryName, value: code },
      state: stateOptions.find((option) => option.value === stateCode) ?? null,
      stateOptions,
    };
  };

  handleFieldChange = (key: string, value: Candidate[keyof Candidate]) => {
    const { candidate } = this.state;
    Object.assign(candidate, { [key]: value });
    this.setState({ candidate }, () => {
      this.setState({ isSubmittedDisabled: !this.validateFormFields() });
    });
  };

  handleJobChange = (value: SelectOption | null) => {
    this.handleFieldChange('job_uuid', value);
    this.handleFieldChange('hirevue_position', null);
    this.fetchPrescreenOptions(value?.value);
  };

  fetchPrescreenOptions = (jobId?: string) => {
    if (!jobId) {
      this.setState({ prescreenOptions: [] });

      return;
    }

    this.setState({ prescreenOptions: [] });

    client
      .query<GetAvailablePrescreensResponse, getAvailablePrescreensVariables>({
        query: GetAvailablePrescreens,
        variables: { jobId },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
      })
      .then(({ data }) => {
        if (this.state.candidate.job_uuid?.value !== jobId) {
          return;
        }

        this.setState({ prescreenOptions: data?.getAvailablePrescreens.map(toPrescreenOption) ?? [] });
      })
      .catch(() => {
        this.setState({
          showMessage: true,
          messageVariant: 'error',
          messageContent: 'Failed to load prescreens for the selected job.',
        });
      });
  };

  handleClientFieldChange = (key: string, value: SelectOption[] | null) => {
    this.handleFieldChange(key, value);
    const { candidate } = this.state;
    const clientHiringCompaniesOptions = this.getClientHiringCompanyOptions();
    const clientHiringCompaniesIds = clientHiringCompaniesOptions.map((option: SelectOption) => option.value);

    candidate.client_hiring_companies = candidate.client_hiring_companies?.filter((chc: SelectOption) =>
      clientHiringCompaniesIds?.includes(chc.value),
    );
    this.setState({ candidate });
  };

  handleCountryChange = (selectedCountry: NameValuePair | null, stateOptions: NameValuePair[]) => {
    this.setState(
      (prevState) => ({
        stateOptions,
        candidate: { ...prevState.candidate, country: selectedCountry, state: null },
      }),
      () => {
        this.setState({ isSubmittedDisabled: !this.validateFormFields() });
      },
    );
  };

  handleAddressSelect = (suggestion: AddressSuggestion) => {
    const state =
      this.state.stateOptions.find(
        (option) =>
          option.value === suggestion.state ||
          `${this.state.candidate.country?.value}-` + suggestion.state === option.value,
      ) ?? null;
    this.setState(
      (prevState) => ({
        candidate: {
          ...prevState.candidate,
          street_address: suggestion.street || '',
          city: suggestion.city || '',
          zip_code: suggestion.zipcode || '',
          state,
        },
      }),
      () => {
        this.setState({ isSubmittedDisabled: !this.validateFormFields() });
      },
    );
  };

  handlePreFillEditCandidateData = () => {
    this.setState({ showLoader: true });
    client
      .query<GetCandidateByIdResponse>({
        query: GetCandidateByIdQuery,
        variables: {
          id: this.props.candidateId,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .then((data) => {
        const candidateData = data?.data?.candidateDetails;
        const contactData = candidateData?.contact;

        const shoreTypePreferences =
          candidateData?.shore_type_preferences?.map((option) => ({
            label: option.shore_type_name ?? '',
            value: option.shore_type_id,
          })) ?? [];

        const workLocationTypePreferences =
          candidateData?.work_location_type_preferences?.map((option) => ({
            label: option.work_location_type_name ?? '',
            value: option.work_location_type_id,
          })) ?? [];

        const previousCountryIndex = allCountries.findIndex(
          ([, countryCode]: [string, string, string[][]]) => countryCode === contactData?.country,
        );
        const previousCountry = previousCountryIndex === -1 ? null : allCountries[previousCountryIndex];

        let previousStateOption = null;
        let stateOptions: NameValuePair[] = [];

        if (previousCountryIndex > -1) {
          const currentCountryStates = allCountries[previousCountryIndex][2];
          stateOptions = currentCountryStates.map(([stateName, stateCode]: [string, string]) => ({
            label: stateName,
            value: stateCode,
          }));
          previousStateOption = stateOptions.find((option) => option.value === contactData?.state) ?? null;
        }

        let jobTitleLabel = candidateData?.job_req?.job_title?.name;
        if (candidateData?.job_req?.alternate_title) {
          jobTitleLabel = `${jobTitleLabel}-${candidateData?.job_req?.alternate_title}`;
        }
        const { isConfirmRehire, candidateId } = this.props;
        const shouldClear = isConfirmRehire && candidateId;

        const candidate: Candidate = {
          id: candidateData?.id ?? undefined,
          first_name: contactData?.first_name ?? '',
          last_name: contactData?.last_name ?? '',
          ssn: contactData?.ssn ?? '',
          email_id: contactData?.email_id ?? '',
          sub_contractor_firm: candidateData?.sub_contractor_firm ?? '',
          summary: candidateData?.summary ?? '',
          candidate_documents: [],
          rate_frequency: {
            label: candidateData?.rate_frequency?.name ?? '',
            value: candidateData?.rate_frequency?.id ?? '',
          },
          rate_frequency_id: candidateData?.rate_frequency?.id ?? '',
          contact_id: contactData?.id ?? '',
          interview_availability: candidateData?.interview_availability ?? '',
          earliest_start_date: candidateData?.earliest_start_date
            ? moment(candidateData.earliest_start_date).tz('UTC', true).format('MM/DD/YYYY')
            : null,
          phone_number: contactData?.phone_number ?? '',
          linkedin_url: contactData?.linkedin_url ?? '',
          skype_id: contactData?.skype_id ?? '',
          status: candidateData?.status ?? '',
          submission_rate: candidateData?.vendor_rate || candidateData?.submission_rate || null,
          mark_up_percentage: candidateData?.mark_up_percentage ?? null,
          estimated_hours: candidateData?.estimated_hours ?? null,
          vendor_id: candidateData?.vendor?.id ?? '',
          location: candidateData?.location ?? '',
          street_address: contactData?.street_address ?? '',
          city: contactData?.city ?? '',
          state: previousStateOption,
          country: previousCountry ? { label: previousCountry[0], value: previousCountry[1] } : null,
          zip_code: contactData?.zip_code ?? '',
          phone_type: contactData?.phone_type ? contactData.phone_type.toUpperCase() : null,
          additional_information: candidateData?.additional_information ?? null,
          shore_type_preferences: shoreTypePreferences,
          work_location_type_preferences: workLocationTypePreferences,
          job_uuid: { label: jobTitleLabel ?? '', value: candidateData?.job_uuid ?? '' },
          hirevue_position: null,
          clients: shouldClear
            ? []
            : (candidateData?.clients?.map((c) => ({
                label: c.name ?? '',
                value: c.id ?? '',
              })) ?? []),
          client_hiring_companies: shouldClear
            ? []
            : (candidateData?.client_hiring_companies?.map((chc) => {
                const sublabel = chc.workspace?.name ? ` (${chc.workspace.name})` : '';

                return { label: (chc.name ?? '') + sublabel, value: chc.id ?? '', clientId: chc.workspace?.id ?? '' };
              }) ?? []),
          client_ids: [],
          client_hiring_company_ids: [],
          work_authorized: booleanToSelectOption(contactData?.work_authorized),
          visa_required: booleanToSelectOption(contactData?.visa_required),
          skills:
            candidateData?.skills?.map((s) => ({
              name: s.name,
              proficiency_rating: s.proficiency_rating ?? null,
              standout_skill: s.standout_skill || false,
              verified: s.verified || false,
              delete_icon: '',
            })) ?? [],
          employment_history:
            candidateData?.employment_history?.map((e) => ({
              id: e.id ?? undefined,
              companyName: e.company_name || '',
              jobTitle: e.job_title || '',
              startDate: e.start_date || '',
              endDate: e.end_date || '',
              additionalInfo: e.additional_information || '',
            })) ?? [],
          education_history:
            candidateData?.education_history?.map((e) => ({
              id: e.id ?? undefined,
              schoolName: e.school_name || '',
              degreeName: e.degree_name || '',
              startDate: e.start_date || '',
              endDate: e.end_date || '',
              gpa: e.gpa || null,
              additionalInfo: e.additional_information || '',
            })) ?? [],
        };

        // Map existing documents into rowData and candidate_documents
        let resumeCount = 0;
        const rowData = this.state.rowData;
        if (candidateData?.candidate_documents) {
          candidateData.candidate_documents.forEach((doc) => {
            if (!doc) return;
            const categoryId = doc.document_category?.id ?? '';
            let dataId = '1';
            rowData.forEach((row: any) => {
              if (row.id === categoryId) {
                row.files.push({ fileName: doc.document_name ?? '', url: doc.document_url ?? '', fileSize: '' });
                dataId = row.files.length.toString();
              }
            });
            candidate.candidate_documents.push({
              document_category_id: categoryId,
              document_name: doc.document_name ?? '',
              document_url: doc.document_url ?? '',
              data_id: dataId,
            });
            if (categoryId === this.state.resumeId) {
              resumeCount++;
            }
          });
        }

        this.setState(
          {
            candidate,
            stateOptions,
            rowData: [],
            resumeCount,
            visitedSteps: [true, true, true, true],
            resumeParseComplete: true,
          },
          () => {
            this.setState({
              rowData,
              isSubmittedDisabled: !this.validateFormFields(),
              showLoader: false,
              editDataLoaded: true,
            });
            this.fetchPrescreenOptions(candidate.job_uuid?.value);
          },
        );
      })
      .catch(() => {
        this.setState({
          showLoader: false,
          showMessage: true,
          messageVariant: 'error',
          messageContent: 'Failed to load candidate data. Please close and try again.',
        });
      });
  };

  getClientHiringCompanyOptions = () => {
    const selectedClients = this.state.candidate?.clients?.map((c: SelectOption) => c.value) ?? [];

    return this.state.clientHiringCompanies.filter((row) => selectedClients.includes(row.clientId));
  };

  getResumeAndUploadsErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    const { resumeCount } = this.state;

    if (resumeCount === 0) {
      errors.resume = REQUIRED_FIELD_MESSAGE;
    } else if (resumeCount !== 1) {
      errors.resume = invalidValueMessage(RESUME_AND_UPLOADS_FIELD_LABELS.resume);
    }

    return errors;
  };

  getCandidateDetailsErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    const { candidate } = this.state;
    const labels = CANDIDATE_DETAILS_FIELD_LABELS;

    if (!candidate.first_name) {
      errors.firstName = REQUIRED_FIELD_MESSAGE;
    } else if (candidate.first_name.length > NAME_MAX_LENGTH) {
      errors.firstName = invalidValueMessage(labels.firstName);
    }

    if (!candidate.last_name) {
      errors.lastName = REQUIRED_FIELD_MESSAGE;
    } else if (candidate.last_name.length > NAME_MAX_LENGTH) {
      errors.lastName = invalidValueMessage(labels.lastName);
    }

    if (!candidate.ssn) {
      errors.ssn = REQUIRED_FIELD_MESSAGE;
    } else if (!isValidNumber(candidate.ssn, SSN_LENGTH)) {
      errors.ssn = invalidValueMessage(labels.ssn);
    }

    if (!candidate.email_id) {
      errors.email = REQUIRED_FIELD_MESSAGE;
    } else if (!isValidEmail(candidate.email_id)) {
      errors.email = invalidValueMessage(labels.email);
    }

    if (!candidate.summary) {
      errors.summary = REQUIRED_FIELD_MESSAGE;
    } else if (candidate.summary.length > SUMMARY_MAX_LENGTH) {
      errors.summary = SUMMARY_MAX_LENGTH_MESSAGE;
    }

    if (candidate.work_authorized === null) {
      errors.workAuthorized = REQUIRED_FIELD_MESSAGE;
    }

    if (candidate.visa_required === null) {
      errors.visaRequired = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.interview_availability) {
      errors.meetingAvailability = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.phone_number) {
      errors.phoneNumber = REQUIRED_FIELD_MESSAGE;
    } else if (!isValidNumber(candidate.phone_number, PHONE_NUMBER_LENGTH)) {
      errors.phoneNumber = PHONE_NUMBER_MESSAGE;
    }

    if (!candidate.country?.value) {
      errors.country = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.city) {
      errors.city = REQUIRED_FIELD_MESSAGE;
    }

    if (candidate.country?.value === 'US' && !candidate.state?.value) {
      errors.state = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.shore_type_preferences?.length) {
      errors.shoreTypePreferences = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.work_location_type_preferences?.length) {
      errors.workLocationTypePreferences = REQUIRED_FIELD_MESSAGE;
    }

    return errors;
  };

  getSkillsAndEducationErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    const { candidate, skillsEducationFormOpen, skillsEducationFormValid, skillsEducationInvalidFields } = this.state;

    if (skillsEducationFormOpen && !skillsEducationFormValid) {
      if (skillsEducationInvalidFields.length) {
        skillsEducationInvalidFields.forEach((label) => {
          errors[label] = REQUIRED_FIELD_MESSAGE;
        });
      } else {
        errors.openForm = REQUIRED_FIELD_MESSAGE;
      }
    }

    const hasInvalidEducation = candidate.education_history?.some(
      (education) =>
        !education.schoolName ||
        !education.degreeName ||
        (education.startDate && education.endDate && moment(education.endDate).isBefore(moment(education.startDate))),
    );
    if (hasInvalidEducation) {
      errors.educationHistory = REQUIRED_FIELD_MESSAGE;
    }

    const hasInvalidEmployment = candidate.employment_history?.some(
      (employment) =>
        !employment.companyName ||
        !employment.jobTitle ||
        !employment.startDate ||
        (!!employment.endDate && moment(employment.endDate).isBefore(moment(employment.startDate))),
    );
    if (hasInvalidEmployment) {
      errors.employmentHistory = REQUIRED_FIELD_MESSAGE;
    }

    return errors;
  };

  getRateAndJobErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    const { candidate } = this.state;

    if (!candidate.submission_rate) {
      errors.rate = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.rate_frequency?.value) {
      errors.rateFrequency = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.earliest_start_date) {
      errors.earliestStartDate = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.job_uuid?.value) {
      errors.job = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.clients?.length) {
      errors.clients = REQUIRED_FIELD_MESSAGE;
    }

    if (!candidate.client_hiring_companies?.length) {
      errors.hiringCompanies = REQUIRED_FIELD_MESSAGE;
    }

    return errors;
  };

  getStepErrors = (stepIndex: number): FieldErrors => {
    switch (stepIndex) {
      case 0:
        return this.getResumeAndUploadsErrors();
      case 1:
        return this.getCandidateDetailsErrors();
      case 2:
        return this.getSkillsAndEducationErrors();
      case 3:
        return this.getRateAndJobErrors();
      default:
        return {};
    }
  };

  validateFormFields = (): boolean => {
    return [1, 2, 3].every((stepIndex) => Object.keys(this.getStepErrors(stepIndex)).length === 0);
  };

  markFieldTouched = (field: string) => {
    this.setState((prevState) => ({ touchedFields: { ...prevState.touchedFields, [field]: true } }));
  };

  markStepFieldsTouched = (stepIndex: number) => {
    const stepFields = STEP_FIELD_LABELS[stepIndex];
    if (!stepFields) return;

    this.setState((prevState) => {
      const touchedFields = { ...prevState.touchedFields };
      Object.keys(stepFields).forEach((field) => {
        touchedFields[field] = true;
      });

      return { touchedFields };
    });
  };

  markStepsAsVisited = (...stepIndices: number[]) => {
    this.setState((prevState) => {
      const newVisitedSteps = [...prevState.visitedSteps] as [boolean, boolean, boolean, boolean];
      stepIndices.forEach((i) => {
        newVisitedSteps[i] = true;
      });

      return { visitedSteps: newVisitedSteps };
    });
  };

  getStepValidationStatus = (stepIndex: number, errors: FieldErrors): StepStatus => {
    if (!this.state.visitedSteps[stepIndex]) return 'unvisited';

    return Object.keys(errors).length === 0 ? 'complete' : 'incomplete';
  };

  isResumeUploaded = (): boolean => {
    return this.state.candidate.candidate_documents.some(
      (docs: CandidateDocument) => docs.document_category_id == this.state.resumeId,
    );
  };

  validateSSN = () => {
    const { candidate } = this.state;
    this.setState({ showLoader: true });

    client
      .query<ValidatesNameAndSsnResponse>({
        query: ValidatesNameAndSsn,
        variables: {
          job_id: candidate.job_uuid?.value || candidate.job_uuid,
          first_name: candidate.first_name,
          last_name: candidate.last_name,
          ssn: candidate.ssn,
          email_id: candidate.email_id,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
      })
      .then((data) => {
        const validationResult = data?.data?.validatesNameAndSsn;
        if (validationResult?.is_valid) {
          if (validationResult?.contact_id) {
            this.handleFieldChange('contact_id', validationResult.contact_id);
          }
          this.submitCandidateForm();
        } else {
          this.setState({
            showLoader: false,
            showMessage: true,
            messageContent: 'This candidate has already been submitted for the selected job.',
            messageVariant: 'error',
          });
        }
      })
      .catch(() => {
        this.setState({
          showLoader: false,
          showMessage: true,
          messageContent: 'Validation failed. Please try again.',
          messageVariant: 'error',
        });
      });
  };

  validateAndSubmit = () => {
    this.setState({ showLoader: true });

    const allFieldsValid = this.validateFormFields();
    const resumeUploaded = this.isResumeUploaded();

    if (!allFieldsValid) {
      this.setState({
        showMessage: true,
        messageContent: 'Please fill in all required fields.',
        messageVariant: 'error',
      });

      return;
    }
    if (!resumeUploaded) {
      this.setState({
        showMessage: true,
        messageContent: 'Please upload a Candidate Resume.',
        messageVariant: 'error',
      });

      return;
    }
    if (this.props.mode === 'edit') {
      this.submitCandidateForm();
    } else {
      this.validateSSN();
    }
  };

  generateCandidateForMutation = () => {
    const candidate = Object.fromEntries(
      Object.entries(this.state.candidate).map(([key, value]) => {
        const obj = value as { value?: string } | null;
        if (key == 'submission_rate') {
          return [key, parseFloat(value as string)];
        }
        if (typeof value == 'string') {
          return [key, value.trim()];
        } else if (obj?.value) {
          return [key, obj.value];
        } else {
          return [key, value];
        }
      }),
    );

    const tempdata: CandidateDocument[] = [];
    Array.isArray(candidate?.candidate_documents) &&
      candidate.candidate_documents.map((data: CandidateDocument) => {
        tempdata.push({
          document_category_id: data.document_category_id,
          document_name: data.document_name,
          document_url: data.document_url.split('?')[0],
        });
      });
    candidate.candidate_documents = tempdata;
    candidate.additional_information = this.state.candidate.additional_information;
    candidate.rate_frequency_id = candidate.rate_frequency;
    candidate.earliest_start_date = candidate.earliest_start_date
      ? moment.utc(candidate.earliest_start_date, 'MM/DD/YYYY', true).toISOString()
      : null;
    candidate.estimated_hours = parseFloat(candidate.estimated_hours);
    candidate.mark_up_percentage = parseFloat(candidate.mark_up_percentage);
    candidate.client_ids = candidate.clients?.map((c: SelectOption) => c.value) ?? [];
    candidate.client_hiring_company_ids =
      candidate.client_hiring_companies?.map((chc: SelectOption) => chc.value) ?? [];
    candidate.work_location_type_preferences =
      candidate.work_location_type_preferences?.map((type: NameValuePair) => type.value) ?? [];
    candidate.shore_type_preferences = candidate.shore_type_preferences?.map((type: NameValuePair) => type.value) ?? [];
    candidate.country_full = this.state.candidate.country?.label ?? '';
    candidate.country = this.state.candidate.country?.value ?? '';
    candidate.state_full = this.state.candidate.state?.label ?? '';
    candidate.state = this.state.candidate.state?.value ?? '';
    candidate.work_authorized = this.state.candidate.work_authorized?.value === 'YES';
    candidate.visa_required = this.state.candidate.visa_required?.value === 'YES';

    candidate.skills = this.state.candidate.skills.map((skill: CandidateSkill) => ({
      name: skill.name,
      proficiency_rating: skill.proficiency_rating,
      verified: skill.verified,
      standout_skill: skill.standout_skill,
    }));
    candidate.employment_history = this.state.candidate.employment_history.map((emp: Employment) => ({
      id: emp.id || undefined,
      company_name: emp.companyName,
      job_title: emp.jobTitle,
      start_date: emp.startDate,
      end_date: emp.endDate || undefined,
      additional_info: emp.additionalInfo || undefined,
    }));
    candidate.education_history = this.state.candidate.education_history.map((edu: EducationEntity) => ({
      id: edu.id || undefined,
      school_name: edu.schoolName,
      degree_name: edu.degreeName,
      start_date: edu.startDate,
      end_date: edu.endDate || undefined,
      gpa: edu.gpa || undefined,
      additional_info: edu.additionalInfo || undefined,
    }));

    if (this.state.prescreenEnabled) {
      candidate.hirevue_position_id = this.state.candidate.hirevue_position?.hirevuePositionId ?? undefined;
      candidate.linked_job_id = this.state.candidate.hirevue_position?.linkedJobId ?? null;
    }

    delete candidate.id;
    delete candidate.job;
    delete candidate.rate_frequency;
    delete candidate.clients;
    delete candidate.client_hiring_companies;
    delete candidate.files;
    delete candidate.hirevue_position;

    return candidate;
  };

  submitCandidateForm = () => {
    const isEdit = this.props.mode === 'edit';
    const input = {
      ...this.generateCandidateForMutation(),
      ...(this.props.contractorId && this.props.workspaceId
        ? { contractor_id: this.props.contractorId, workspace_id: this.props.workspaceId }
        : {}),
    };
    const variables = isEdit ? { id: this.props.candidateId, input } : { input };
    client
      .mutate({
        mutation: isEdit ? EditCandidate : SubmitCandidate,
        variables,
      })
      .then((response) => {
        if (response?.data) {
          this.setState({ showLoader: false });
          this.clearModal();
          this.props.handleClose(true);
          if (this.props.isConfirmRehire) {
            history.push(`/marketplace`);
          }
        } else {
          this.handleMutationError();
        }
      })
      .catch(() => {
        this.handleMutationError();
      });
  };

  handleMutationError = () => {
    this.setState({
      showLoader: false,
      messageContent: 'Something went wrong, Please try again!',
      messageVariant: 'error',
      showMessage: true,
    });
  };

  clearModal = () => {
    const rowData = this.state.emptyRowData.map((doc) => ({
      ...doc,
      files: [],
    }));
    this.setState({
      candidate: this.initializeCandidate(),
      currentStep: 0,
      resumeCount: 0,
      isSubmittedDisabled: true,
      rowData,
      visitedSteps: [false, false, false, false],
      learnMoreDrawerOpen: false,
      skillsEducationFormOpen: false,
      skillsEducationFormValid: true,
      skillsEducationInvalidFields: [],
      resumeParseComplete: false,
      resumeParseError: false,
      editDataLoaded: this.props.mode !== 'edit',
      touchedFields: {},
      rehirePrefill: null,
    });
  };

  // Document management
  mapUploadedFilesToState = (id: string, fileName: string, url: string, fileIndex: number, fileSize: string) => {
    const data = this.state.candidate;
    data.candidate_documents.push({
      document_category_id: id,
      document_name: fileName,
      document_url: url,
      data_id: (fileIndex + 1).toString(),
    });
    const rowData = this.state.rowData;
    rowData.forEach((row: any) => {
      if (row.id === id) {
        row.files.push({ fileName, url, fileSize });
      }
    });
    if (id === this.state.resumeId) {
      this.setState({ resumeCount: this.state.resumeCount + 1 });
    }
    this.setState({ candidate: data, rowData });
  };

  deleteFileUploaded = (id: string, fileIndex: number) => {
    const data = this.state.candidate;
    const rowData = this.state.rowData;
    const tempDocument: CandidateDocument[] = [];
    this.setState({ rowData: [] });

    data.candidate_documents?.forEach((document: Readonly<CandidateDocument>) => {
      if (!(document.document_category_id === id && Number(document.data_id) === fileIndex + 1)) {
        tempDocument.push(document);
      }
    });
    data.candidate_documents = tempDocument;

    if (id === this.state.resumeId && this.state.resumeCount > 0) {
      this.setState((prevState) => ({
        resumeCount: prevState.resumeCount - 1,
        // Allow re-parsing only if the previous parse failed
        resumeParseComplete: prevState.resumeParseError ? false : prevState.resumeParseComplete,
        resumeParseError: false,
      }));
    }

    rowData.forEach((row: any) => {
      if (row.id === id) {
        row.files.splice(fileIndex, 1);
      }
    });

    this.setState({ candidate: data, rowData: [] }, () => {
      this.setState({ rowData });
    });
  };

  parseResume = (resumeUrl: string) => {
    if (this.state.resumeParseComplete) return;

    client
      .mutate<ParseResumeType>({
        mutation: ParseResume,
        variables: {
          input: {
            document_url: resumeUrl,
          },
        },
      })
      .then((response) => {
        const parsedResumeData = response.data?.parseResume;
        if (parsedResumeData) {
          this.setState((prevState) => {
            const candidate = { ...prevState.candidate };
            candidate.first_name = parsedResumeData.first_name ?? candidate.first_name;
            candidate.last_name = parsedResumeData.last_name ?? candidate.last_name;
            candidate.email_id = parsedResumeData.email ?? candidate.email_id;
            candidate.phone_number = getOnlyNumbers(parsedResumeData.phone ?? '') || candidate.phone_number;
            candidate.linkedin_url = parsedResumeData.linkedin ?? candidate.linkedin_url;
            candidate.summary = parsedResumeData.summary ?? candidate.summary;

            candidate.skills =
              parsedResumeData.skills?.map((skillName: string) => ({
                name: skillName,
                proficiency_rating: null,
                verified: false,
                standout_skill: false,
                delete_icon: '',
              })) ?? [];

            const address = parsedResumeData.address;
            if (address) {
              candidate.street_address = address.street ?? candidate.street_address;
              candidate.city = address.city ?? candidate.city;
              candidate.zip_code = address.zip_code ?? candidate.zip_code;

              const countryIndex = allCountries.findIndex(
                ([, countryCode]: [string, string, string[][]]) => countryCode === address?.country,
              );

              const previousCountry = countryIndex === -1 ? null : allCountries[countryIndex];
              let previousStateOption = null;

              if (countryIndex > -1) {
                const currentCountryStates = allCountries[countryIndex][2];
                const stateOptions: NameValuePair[] = currentCountryStates.map(
                  ([stateName, stateCode]: [string, string]) => ({
                    label: stateName,
                    value: stateCode,
                  }),
                );

                previousStateOption = stateOptions.find((option) => option.value === address?.state) ?? null;
                this.setState({ stateOptions });
              }

              candidate.country = previousCountry
                ? { label: previousCountry[0], value: previousCountry[1] }
                : candidate.country;
              candidate.state = previousStateOption ?? candidate.state;
            }

            candidate.education_history =
              parsedResumeData.education?.map((education: ParseResume_parseResume_education) => ({
                schoolName: education.school_name ?? '',
                degreeName: education.degree_name ?? '',
                startDate:
                  education.start_date && moment(education.start_date).isValid()
                    ? moment(education.start_date).format('MM/DD/YYYY')
                    : '',
                endDate:
                  education.end_date && moment(education.end_date).isValid()
                    ? moment(education.end_date).format('MM/DD/YYYY')
                    : '',
                gpa: education.gpa ? Number.parseFloat(education.gpa) : null,
                additionalInfo: '',
              })) ?? candidate.education_history;

            candidate.employment_history =
              parsedResumeData.experience?.map((employment: ParseResume_parseResume_experience) => {
                let endDate = '';
                if (employment.end_date && employment.end_date.toLowerCase() !== 'current') {
                  const parsedEndDate = moment(employment.end_date);
                  endDate = parsedEndDate.isValid() ? parsedEndDate.format('MM/DD/YYYY') : '';
                }

                const parsedStartDate = employment.start_date ? moment(employment.start_date) : null;

                return {
                  companyName: employment.company_name ?? '',
                  jobTitle: employment.title ?? '',
                  startDate: parsedStartDate?.isValid() ? parsedStartDate.format('MM/DD/YYYY') : '',
                  endDate,
                  additionalInfo: employment?.additional_information ?? '',
                };
              }) ?? candidate.employment_history;

            const newVisitedSteps = [...prevState.visitedSteps] as [boolean, boolean, boolean, boolean];
            newVisitedSteps[0] = true;

            return {
              candidate,
              resumeParseComplete: true,
              resumeParseError: false,
              visitedSteps: newVisitedSteps,
            };
          });
        } else {
          this.setState((prevState) => {
            const newVisitedSteps = [...prevState.visitedSteps] as [boolean, boolean, boolean, boolean];
            newVisitedSteps[0] = true;

            return { resumeParseComplete: true, resumeParseError: true, visitedSteps: newVisitedSteps };
          });
        }
      })
      .catch(() => {
        this.setState((prevState) => {
          const newVisitedSteps = [...prevState.visitedSteps] as [boolean, boolean, boolean, boolean];
          newVisitedSteps[0] = true;

          return { resumeParseComplete: true, resumeParseError: true, visitedSteps: newVisitedSteps };
        });
      });
  };

  showSnackBar = (variant: SnackBarVariant, message: string) => {
    this.setState({ showMessage: true, messageVariant: variant, messageContent: message });
  };

  scrollToTopOfModal = () => {
    const modalBody = document.querySelector('#modalBody');
    modalBody?.scroll({ top: 0, behavior: 'smooth' });
  };

  handlePreviousStepClick = () => {
    this.markStepFieldsTouched(this.state.currentStep);
    this.markStepsAsVisited(this.state.currentStep, Math.max(0, this.state.currentStep - 1));
    this.setState(
      (prev) => ({ currentStep: Math.max(0, prev.currentStep - 1) }),
      () => {
        this.scrollToTopOfModal();
        this.setState({ isSubmittedDisabled: !this.validateFormFields() });
      },
    );
  };

  handleNextStepClick = () => {
    this.markStepFieldsTouched(this.state.currentStep);
    this.markStepsAsVisited(this.state.currentStep, Math.min(3, this.state.currentStep + 1));
    this.setState(
      (prev) => ({ currentStep: Math.min(3, prev.currentStep + 1) }),
      () => {
        this.scrollToTopOfModal();
        this.setState({ isSubmittedDisabled: !this.validateFormFields() });
      },
    );
  };

  render() {
    const { open, handleClose, classes, mode, isConfirmRehire } = this.props;
    const { candidate, currentStep, showLoader, isSubmittedDisabled, editDataLoaded, touchedFields } = this.state;
    const isEdit = mode === 'edit';

    const resumeAndUploadsErrors = this.getStepErrors(0);
    const candidateDetailsErrors = this.getStepErrors(1);
    const skillsAndEducationErrors = this.getStepErrors(2);
    const rateAndJobErrors = this.getStepErrors(3);

    // Step 1: Candidate Details
    const detailValues = {
      firstName: candidate.first_name || '',
      lastName: candidate.last_name || '',
      ssn: candidate.ssn || '',
      email: candidate.email_id || '',
      subcontractorFirm: candidate.sub_contractor_firm || '',
      summary: candidate.summary || '',
      additionalInformation: candidate.additional_information || '',
      workAuthorized: candidate.work_authorized,
      visaRequired: candidate.visa_required,
      meetingAvailability: candidate.interview_availability || '',
      linkedIn: candidate.linkedin_url || '',
      phoneNumber: candidate.phone_number || '',
      phoneType: candidate.phone_type ? phoneTypeOptions.find((o) => o.value === candidate.phone_type) || null : null,
      country: candidate.country,
      streetAddress: candidate.street_address || '',
      city: candidate.city || '',
      state: candidate.state,
      postalCode: candidate.zip_code || '',
      shoreTypePreferences: candidate.shore_type_preferences,
      workLocationTypePreferences: candidate.work_location_type_preferences,
    };

    const detailOptions = {
      workAuthorized: workAuthorizedOptions,
      visaRequired: visaRequiredOptions,
      phoneType: phoneTypeOptions,
      state: this.state.stateOptions,
      shoreType: this.state.allowedShoreTypes as SelectOption[],
      workLocationType: this.state.workLocationTypeOptions as SelectOption[],
    };

    const detailChangeHandlers = {
      firstNameOnChange: (v: string) => this.handleFieldChange('first_name', v),
      lastNameOnChange: (v: string) => this.handleFieldChange('last_name', v),
      ssnOnChange: (v: string) => this.handleFieldChange('ssn', v),
      emailOnChange: (v: string) => this.handleFieldChange('email_id', v),
      subcontractorFirmOnChange: (v: string) => this.handleFieldChange('sub_contractor_firm', v),
      summaryOnChange: (v: string) => this.handleFieldChange('summary', v),
      additionalInformationOnChange: (v: string) => this.handleFieldChange('additional_information', v),
      workAuthorizedOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('work_authorized', v as SelectOption | null),
      visaRequiredOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('visa_required', v as SelectOption | null),
      meetingAvailabilityOnChange: (v: string) => this.handleFieldChange('interview_availability', v),
      linkedInOnChange: (v: string) => this.handleFieldChange('linkedin_url', v),
      phoneNumberOnChange: (v: string) => this.handleFieldChange('phone_number', v),
      phoneTypeOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('phone_type', Array.isArray(v) ? null : v?.value || null),
      countryOnChange: this.handleCountryChange,
      streetAddressOnChange: (v: string) => this.handleFieldChange('street_address', v),
      cityOnChange: (v: string) => this.handleFieldChange('city', v),
      stateOnChange: (v: SelectOptionChangeParam) => this.handleFieldChange('state', v as NameValuePair | null),
      postalCodeOnChange: (v: string) => this.handleFieldChange('zip_code', v),
      shoreTypePreferencesOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('shore_type_preferences', v as SelectOption[]),
      workLocationTypePreferencesOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('work_location_type_preferences', v as SelectOption[]),
    };

    // Step 2: Rate and Job
    const rateAndJobValues = {
      rate: candidate.submission_rate,
      rateFrequency: candidate.rate_frequency?.value ? candidate.rate_frequency : null,
      markupPercentage: candidate.mark_up_percentage,
      earliestStartDate: candidate.earliest_start_date
        ? moment(candidate.earliest_start_date).format('MM/DD/YYYY')
        : '',
      estimatedHours: candidate.estimated_hours,
      clients: candidate.clients,
      job: candidate.job_uuid?.value ? candidate.job_uuid : null,
      prescreen: candidate.hirevue_position,
      hiringCompanies: candidate.client_hiring_companies,
    };

    const rateAndJobOptions = {
      rateFrequency: this.state.rateFrequencyOptions,
      shoreType: this.state.allowedShoreTypes as SelectOption[],
      workLocationType: this.state.workLocationTypeOptions as SelectOption[],
      clients: this.props.noWorkspaceAccess ? candidate.clients : this.state.allowedClients,
      jobTitles: this.state.jobTitleOptions,
      prescreens: this.state.prescreenOptions,
      hiringCompanies: this.getClientHiringCompanyOptions(),
    };

    const rateAndJobChangeHandlers = {
      rateOnChange: (v: string) => this.handleFieldChange('submission_rate', v),
      rateFrequencyOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('rate_frequency', v as SelectOption | null),
      markupPercentageOnChange: (v: string) => this.handleFieldChange('mark_up_percentage', v),
      earliestStartDateOnChange: (v: string) => this.handleFieldChange('earliest_start_date', v),
      estimatedHoursOnChange: (v: string) => this.handleFieldChange('estimated_hours', v),
      clientsOnChange: (v: SelectOptionChangeParam) =>
        this.handleClientFieldChange('clients', v as SelectOption[] | null),
      jobOnChange: (v: SelectOptionChangeParam) => this.handleJobChange(v as SelectOption | null),
      prescreenOnChange: (v: SelectOptionChangeParam | PrescreenOption) => {
        this.handleFieldChange('hirevue_position', v as PrescreenOption | null);
        this.handleFieldChange('linked_job_id', (v as PrescreenOption)?.linkedJobId ?? null);
      },
      hiringCompaniesOnChange: (v: SelectOptionChangeParam) =>
        this.handleFieldChange('client_hiring_companies', v as SelectOption[]),
    };

    const setSkillCategory = (skill: CandidateSkill, standout: boolean): CandidateSkill => ({
      ...skill,
      standout_skill: standout,
      proficiency_rating: standout ? (skill.proficiency_rating ?? 1) : null,
    });

    const steps: StepDef[] = [
      {
        title: 'Resume & Uploads',
        status: this.getStepValidationStatus(0, resumeAndUploadsErrors),
        invalidFields: getInvalidFieldNames(resumeAndUploadsErrors, RESUME_AND_UPLOADS_FIELD_LABELS),
        content: (
          <ResumeAndUploads
            resumeError={resumeAndUploadsErrors.resume}
            resumeTouched={touchedFields.resume}
            documentRowData={this.state.rowData}
            addFile={this.mapUploadedFilesToState}
            deleteFile={this.deleteFileUploaded}
            isEdit={isEdit}
            onUploadError={() => {
              this.setState({
                showMessage: true,
                messageContent: 'File upload failed. Please try again.',
                messageVariant: 'error',
              });
            }}
            parseResume={this.parseResume}
            resumeParseComplete={this.state.resumeParseComplete}
            resumeParseError={this.state.resumeParseError}
            isConfirmRehire={isConfirmRehire || !!this.state.rehirePrefill}
            candidateName={[candidate.first_name, candidate.last_name].filter(Boolean).join(' ')}
          />
        ),
      },
      {
        title: 'Candidate Details',
        status: this.getStepValidationStatus(1, candidateDetailsErrors),
        invalidFields: getInvalidFieldNames(candidateDetailsErrors, CANDIDATE_DETAILS_FIELD_LABELS),
        content: (
          <CandidateDetails
            values={detailValues}
            options={detailOptions}
            changeHandlers={detailChangeHandlers}
            streetAddressAutocompleteSelect={this.handleAddressSelect}
            errors={candidateDetailsErrors}
            touchedFields={touchedFields}
            onFieldBlur={this.markFieldTouched}
          />
        ),
      },
      {
        title: 'Skills & Education',
        status: this.getStepValidationStatus(2, skillsAndEducationErrors),
        invalidFields: getInvalidFieldNames(skillsAndEducationErrors, SKILLS_AND_EDUCATION_FIELD_LABELS),
        content: (
          <SkillsAndEducation
            values={{
              candidateSkills: candidate.skills,
              employmentHistory: candidate.employment_history,
              educationHistory: candidate.education_history,
            }}
            changeHandlers={{
              onAddSkill: (skill: { name: string }, standout: boolean) => {
                const { candidate } = this.state;
                const existingSkills = candidate.skills ?? [];
                const skillName = skill.name.toLowerCase();
                if (existingSkills.some((s) => s.name.toLowerCase() === skillName)) {
                  const updatedSkills = existingSkills.map((s) =>
                    s.name.toLowerCase() === skillName ? setSkillCategory(s, standout) : s,
                  );
                  this.setState({ candidate: { ...candidate, skills: updatedSkills } });
                  return;
                }

                const newSkill: CandidateSkill = {
                  name: skill.name,
                  proficiency_rating: standout ? 1 : null,
                  standout_skill: standout,
                  verified: false,
                  delete_icon: '',
                };
                this.setState({ candidate: { ...candidate, skills: [...existingSkills, newSkill] } });
              },
              onChangeSkillProficiency: (skillName: string, proficiency: number) => {
                const { candidate } = this.state;
                if (candidate.skills) {
                  const updatedSkills = candidate.skills.map((s) =>
                    s.name === skillName ? { ...s, proficiency_rating: proficiency } : s,
                  );
                  this.setState({ candidate: { ...candidate, skills: updatedSkills } });
                }
              },
              onMoveSkills: (skillNames: string[], standout: boolean) => {
                const { candidate } = this.state;
                if (candidate.skills) {
                  const updatedSkills = candidate.skills.map((s) =>
                    skillNames.includes(s.name) ? setSkillCategory(s, standout) : s,
                  );
                  this.setState({ candidate: { ...candidate, skills: updatedSkills } });
                }
              },
              onDeleteSkills: (skillNames: string[]) => {
                const { candidate } = this.state;
                if (candidate.skills) {
                  const updatedSkills = candidate.skills.filter((s) => !skillNames.includes(s.name));
                  this.setState({ candidate: { ...candidate, skills: updatedSkills } });
                }
              },
              onAddEmployment: (employment: Employment) => {
                this.handleFieldChange('employment_history', [...candidate.employment_history, employment]);
              },
              onEditEmployment: (index: number, employment: Employment) => {
                const updated = [...candidate.employment_history];
                updated[index] = employment;
                this.handleFieldChange('employment_history', updated);
              },
              onDeleteEmployment: (index: number) => {
                const updated = candidate.employment_history.filter((_, i) => i !== index);
                this.handleFieldChange('employment_history', updated);
              },
              onAddEducation: (education: EducationEntity) => {
                this.handleFieldChange('education_history', [...candidate.education_history, education]);
              },
              onEditEducation: (index: number, education: EducationEntity) => {
                const updated = [...candidate.education_history];
                updated[index] = education;
                this.handleFieldChange('education_history', updated);
              },
              onDeleteEducation: (index: number) => {
                const updated = candidate.education_history.filter((_, i) => i !== index);
                this.handleFieldChange('education_history', updated);
              },
            }}
            onClickLearnMore={() => this.setState({ learnMoreDrawerOpen: true })}
            onFormOpenChange={(isOpen) => {
              this.setState(
                (prevState: CandidateModalState) => ({
                  skillsEducationFormOpen: isOpen,
                  skillsEducationFormValid: isOpen ? prevState.skillsEducationFormValid : true,
                  skillsEducationInvalidFields: isOpen ? prevState.skillsEducationInvalidFields : [],
                }),
                () => {
                  this.setState({ isSubmittedDisabled: !this.validateFormFields() });
                },
              );
            }}
            onFormValidityChange={(isValid, invalidFields) => {
              this.setState({ skillsEducationFormValid: isValid, skillsEducationInvalidFields: invalidFields }, () => {
                this.setState({ isSubmittedDisabled: !this.validateFormFields() });
              });
            }}
          />
        ),
      },
      {
        title: 'Rate & Job',
        status: this.getStepValidationStatus(3, rateAndJobErrors),
        invalidFields: getInvalidFieldNames(rateAndJobErrors, RATE_AND_JOB_FIELD_LABELS),
        content: (
          <RateAndJob
            noWorkspaceAccess={this.props.noWorkspaceAccess}
            values={rateAndJobValues}
            options={rateAndJobOptions}
            changeHandlers={rateAndJobChangeHandlers}
            isEdit={isEdit}
            isConfirmRehire={isConfirmRehire}
            errors={rateAndJobErrors}
            touchedFields={touchedFields}
            onFieldBlur={this.markFieldTouched}
          />
        ),
      },
    ];

    const footerContent = (
      <Grid className={classes.footerContainer}>
        <Grid container justify={'space-between'} component={'div'} className={classes.formFooter}>
          <div>
            {currentStep > 0 && (
              <TnButton
                variant={'outlined'}
                color={'secondary'}
                value={steps[currentStep - 1].title}
                className={classes.button}
                onClick={this.handlePreviousStepClick}
                startIcon={<ArrowBackIos style={{ height: 12 }} />}
                disabled={!editDataLoaded}
              />
            )}
          </div>
          <Grid className={classes.rightButtonGroup}>
            {currentStep < steps.length - 1 && (
              <TnButton
                variant={'contained'}
                color={'primary'}
                value={steps[currentStep + 1].title}
                className={classes.button}
                onClick={this.handleNextStepClick}
                disabled={!this.state.resumeParseComplete || !editDataLoaded}
                endIcon={<ArrowForwardIos style={{ height: 12 }} />}
              />
            )}
            {currentStep === steps.length - 1 && (
              <TnButton
                value={isEdit ? 'Save Candidate' : 'Submit Candidate'}
                variant={'contained'}
                color={'primary'}
                disabled={isSubmittedDisabled || this.state.resumeCount !== 1 || showLoader}
                onClick={this.validateAndSubmit}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    );

    const modalTitleContent = () => {
      if (isConfirmRehire) {
        return isEdit ? 'Edit Marketplace Profile' : 'Create Marketplace Profile';
      } else {
        return isEdit ? 'Edit Candidate' : 'Add Candidate';
      }
    };

    return (
      <>
        <TnModal
          large
          open={open}
          handleClose={() => {
            this.clearModal();
            handleClose(false);
          }}
          headerLabel={modalTitleContent()}
          footerContent={footerContent}
          headerContent={
            <Stepper
              steps={steps}
              currentStep={currentStep}
              onChange={(step) => {
                this.markStepFieldsTouched(currentStep);
                this.markStepsAsVisited(currentStep, step);
                this.setState({ currentStep: step }, () => {
                  this.scrollToTopOfModal();
                  this.setState({ isSubmittedDisabled: !this.validateFormFields() });
                });
              }}
              disabled={!this.state.resumeParseComplete || !editDataLoaded}
            />
          }
        >
          <div style={{ marginTop: 56 }}>{steps[currentStep].content}</div>
          {showLoader && <Loader />}
        </TnModal>
        <TnSnackBar
          open={this.state.showMessage}
          variant={this.state.messageVariant}
          message={this.state.messageContent}
          handleClose={() => this.setState({ showMessage: false })}
        />
        <SkillRatingsLearnMoreDrawer
          open={this.state.learnMoreDrawerOpen}
          onClose={() => this.setState({ learnMoreDrawerOpen: false })}
        />
      </>
    );
  }
}

export default withStyles(() => ({
  footerContainer: {
    position: 'fixed' as const,
    width: '100%',
    bottom: 0,
    left: 0,
    backgroundColor: 'white',
    borderTop: '1px solid #E0E0E0',
    zIndex: 10000,
    flexShrink: 0,
  },
  formFooter: {
    padding: '16px',
  },
  button: {
    marginRight: 8,
    fontFamily: 'Avenir Heavy, Arial, Verdana, sans-serif',
    fontWeight: 800,
  },
  rightButtonGroup: {
    display: 'flex',
    flexDirection: 'row' as const,
    gridGap: '8px',
  },
}))(CandidateModal);
